import { useEffect, useMemo } from 'react';
import {  } from '../../../../api/hr';
import { leaveAvailable } from '../../../../lib/leave-balance';
import { Check, Clock, LogOut } from '../../../../icons';
import { useClockTick, formatClock } from '../../../../hooks/useClockTick';
import { getDateLocale } from '../../../../lib/base-i18n';
import { useWorkStatusStore } from '../../../../store/workStatusStore';
import { isCheckOutLocked } from '../../../../lib/work-status';
import { statusLabel } from '../../../../components/StatusDropdown';
import { WORK_STATUS_KEYS } from '../../../../lib/work-status';
import { WidgetCard } from '../WidgetCard';
import { useWidgetActions } from '../widget-context';
import { useLeaveSummary, useOvertime, useMonthDailyWork, useWorkPolicy } from '../widget-queries';
import { leaveTotalText } from '../../../../lib/leave-amount';
// 대시보드 시안 v2 V3b (2026-09-16) — 근태 관리가 보이는 항목을 읽는 판(아래 끝). 깃발이 꺼져 있으면 옛 판 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../../lib/layout-shell';
import { useWidgetItems } from '../use-widget-items';
import { useAttendanceSummary, useDailyWorkMonth } from '../widget-queries';

/**
 * 시계 + 출·퇴근 기록 + 버튼 — 근태 계열 두 위젯이 공유하는 상단 블록.
 *
 * **2026-08-23 — 서버 기록으로 옮겼다**(근태 1단계). 종전에는 이 위젯만 브라우저 저장
 * (`lib/attendance-local`)을 봐서, 상태바에서 찍은 출근이 여기 안 보이고 여기서 찍은 퇴근이
 * 근태에 안 남았다. **같은 사실이 두 화면에서 달랐다.**
 *
 * 이제 상태바와 **같은 스토어**(`workStatusStore`)를 통해 같은 서버 기록을 읽고 쓴다 — 한쪽에서
 * 찍으면 다른 쪽도 그 자리에서 바뀌고, 기기를 옮겨도 따라온다.
 * (스토어가 로컬 기록에도 그대로 미러링하므로 옛 저장을 읽던 코드도 안 깨진다.)
 */
function PunchBlock({ compact = false }: { compact?: boolean }) {
  const tick = useClockTick();
  const today = useWorkStatusStore((s) => s.today);
  const hydrated = useWorkStatusStore((s) => s.hydrated);
  // 오늘이 간주 출근일(승인된 출장·외근)이면 퇴근이 열린다 — 근태 D2, 2026-08-24.
  const deemed = useWorkStatusStore((s) => s.deemed);
  const pending = useWorkStatusStore((s) => s.pending);
  const error = useWorkStatusStore((s) => s.error);
  const hydrate = useWorkStatusStore((s) => s.hydrate);
  const select = useWorkStatusStore((s) => s.select);

  // 이미 읽어 둔 기록이 있으면 다시 묻지 않는다 — 근태 위젯 둘을 함께 얹어도 조회는 1회다.
  useEffect(() => { if (!hydrated) void hydrate(); }, [hydrated, hydrate]);

  /* 오늘의 근무제 이름 — 날짜 줄에 붙는다(시안).
     ★ 날짜 줄이 없는 compact(근태 관리)에서는 **부르지도 않는다** — 안 쓸 것을 받아 오지 않는다. */
  const policy = useWorkPolicy(!compact);
  const scheduleName = policy.data?.schedule?.name ?? null;

  const rec = { checkIn: today.checkIn, checkOut: today.checkOut };
  const onIn = () => { void select('work'); };
  const onOut = () => { void select('off'); };
  // 퇴근은 출근한 사람만 — 상태바·프로필 팝오버와 같은 판정(2026-08-23).
  const offLocked = isCheckOutLocked({ hydrated, checkIn: today.checkIn, deemed: Boolean(deemed) });
  const now = new Date(tick * 1000);

  return (
    <>
      <div className="dw-clock">
        <div className={`dw-clock__time${compact ? ' dw-clock__time--sm' : ''}`}>{formatClock(tick)}</div>
        {!compact && (
          <div className="dw-clock__date">
            {now.toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })}
            {/* 시안의 「· 유연근무」 — 오늘의 근무제. **못 받으면 아예 안 그린다**(빈 칸을 남기지 않는다). */}
            {scheduleName && <span className="dw-clock__sched"> · {scheduleName}</span>}
          </div>
        )}
      </div>
      <div className="dw-punch">
        <div className="dw-punch__cell">
          <div className="dw-punch__k">출근</div>
          <div className="dw-punch__v">{rec.checkIn ?? '--:--'}</div>
        </div>
        <div className="dw-punch__cell">
          <div className="dw-punch__k">퇴근</div>
          <div className={`dw-punch__v${rec.checkOut ? '' : ' dw-punch__v--empty'}`}>{rec.checkOut ?? '--:--'}</div>
        </div>
      </div>
      <div className="dw-punch__actions">
        {rec.checkIn
          ? (
            <button type="button" className="dw-btn dw-btn--done" disabled>
              <Check size={14} aria-hidden />출근 완료
            </button>
          )
          : (
            <button type="button" className="dw-btn" onClick={onIn} disabled={pending !== null}>
              <Clock size={14} aria-hidden />{pending === 'work' ? '기록 중…' : '출근하기'}
            </button>
          )}
        {/* 퇴근은 출근한 사람만 (2026-08-23 · 근태 2차 설계 화면 E). 서버도 같은 규칙이라
            (NOT_CHECKED_IN 400) 눌러 봐야 알던 것을 버튼이 먼저 말해 준다. */}
        <button
          type="button"
          className="dw-btn dw-btn--primary"
          onClick={onOut}
          disabled={Boolean(rec.checkOut) || offLocked || pending !== null}
          title={offLocked ? '출근해야 열립니다' : undefined}
        >
          <LogOut size={14} aria-hidden />
          {rec.checkOut ? '퇴근 완료' : pending === 'off' ? '기록 중…' : '퇴근하기'}
        </button>
      </div>
      {/* 실패는 삼키지 않는다 — 출퇴근은 사실 기록이라 안 남았으면 그렇게 말해야 한다. */}
      {error && <p className="dw-note" role="alert">{error}</p>}
    </>
  );
}

/**
 * 근태 위젯 (#2a) — 현재 시각 · 출퇴근 시각 · 출퇴근 버튼.
 *
 * 기록은 **서버 근태**(`/hr/attendance/*`)에 남는다 — 2026-08-23 이관. 상태바 근무 상태와
 * 같은 스토어를 쓰므로 어느 쪽에서 찍어도 둘 다 같은 값을 보여 준다.
 */
export function AttendanceWidget() {
  const { openAttendanceRequest, go } = useWidgetActions();
  const status = useWorkStatusStore((s) => s.status);
  const pending = useWorkStatusStore((s) => s.pending);
  const select = useWorkStatusStore((s) => s.select);
  const daily = useMonthDailyWork();

  /**
   * 시안 화면 L ③ 이 그린 것 — **이번 주 기록**과 **이번 달 지각·조퇴·연장**.
   *
   * ★★★ 전부 «날별 집계»(`/hr/attendance/daily`)에서 온다. 새 API 를 만들지 않았다 —
   *     이미 오는 값을 화면이 안 보고 있었을 뿐이다.
   * ★★★ **옛 서버는 이 라우트가 없다**(404 → `null`). 그때는 이 절들을 **아예 안 그린다** —
   *     0 으로 그리면 「지각이 없었다」가 되어 사실과 다르다.
   */
  const week = useMemo(() => {
    const rows = daily.data?.days ?? [];
    if (rows.length === 0) return null;
    const base = new Date();
    const from = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    from.setDate(from.getDate() - ((from.getDay() + 6) % 7));
    const days = rows.filter((d) => {
      const t = new Date(`${d.date}T00:00:00`);
      return t >= from && t < new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7);
    });
    const credited = days.reduce((n, d) => n + d.creditedMinutes, 0);
    const peak = Math.max(1, ...days.map((d) => d.creditedMinutes + d.overtimeMinutes));
    return { days, credited, peak, from };
  }, [daily.data]);

  const month = useMemo(() => {
    const rows = daily.data?.days;
    if (!rows) return null;
    return {
      overtime: rows.reduce((n, d) => n + d.overtimeMinutes, 0),
      unclaimed: rows.reduce((n, d) => n + d.unclaimedMinutes, 0),
      needsClose: rows.filter((d) => d.needsClose).length,
    };
  }, [daily.data]);

  const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;

  return (
    <WidgetCard
      title="근태"
      /* 시안(axi-dashboard-v2)의 머리 — 지금 근무 상태를 칩으로 먼저 말한다.
         ★ 이름은 **우리 것을 쓴다**(출근중 · 회의중 …). 시안의 「근무 중 · 회의 · 외근 · 휴식」로 바꾸면
           같은 스토어를 쓰는 **상태바 · 프로필까지 함께 흔들린다**(2026-09-16 관제 결정 — 시안 고칠 자리).
         ★ 고르는 곳은 아래 「근무 상태」 줄 그대로다 — 여기는 **보여 주기만** 한다. */
      tail={status ? <span className="dw-chip">{statusLabel(status)}</span> : null}
      link={{ label: '신청', onClick: () => openAttendanceRequest('punch') }}
    >
      <div className="dw-stack">
        <PunchBlock />

        {/* ★ 근무 상태 — 상태바·프로필과 **같은 스토어**다. 어디서 고르든 같은 값이 된다. */}
        <div className="wi-att__row">
          <span className="wi-att__k">근무 상태</span>
          <span className="wi-att__states">
            {WORK_STATUS_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                className={`wi-att__state${status === k ? ' is-on' : ''}`}
                disabled={pending !== null}
                onClick={() => { void select(k); }}
              >
                {statusLabel(k)}
              </button>
            ))}
          </span>
        </div>

        {week && (
          <div>
            <div className="av-legend wi-att__legend">
              <span>이번 주 누적</span>
              <b>{hm(week.credited)}</b>
            </div>
            {/* 요일 막대 — 소정(파랑) 위에 연장(보라)을 얹는다. 시안 화면 L ③ 과 같은 모양. */}
            <div className="wi-week" aria-hidden>
              {Array.from({ length: 5 }, (_, i) => {
                const d = new Date(week.from.getFullYear(), week.from.getMonth(), week.from.getDate() + i);
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const row = week.days.find((x) => x.date === iso);
                const reg = row ? (row.creditedMinutes / week.peak) * 100 : 0;
                const ot = row ? (row.overtimeMinutes / week.peak) * 100 : 0;
                return (
                  <span key={iso} className={`wi-week__col${row?.needsClose ? ' is-warn' : ''}`}>
                    <i className="wi-week__ot" style={{ height: `${ot}%` }} />
                    <i className="wi-week__reg" style={{ height: `${reg}%` }} />
                    <b>{['월', '화', '수', '목', '금'][i]}</b>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {month && (
          <div className="wi-att__month">
            <button type="button" className="wi-att__m" onClick={() => go('/erp/attendance')}>
              <b>{hm(month.overtime)}</b><span>이번 달 연장</span>
            </button>
            <button type="button" className="wi-att__m" onClick={() => go('/erp/attendance')}>
              <b>{hm(month.unclaimed)}</b><span>신청 안 한 시간</span>
            </button>
            <button type="button" className="wi-att__m" onClick={() => go('/erp/attendance')}>
              <b>{month.needsClose}</b><span>퇴근 누락</span>
            </button>
          </div>
        )}

        <p className="dw-note">출퇴근 기록은 근태에 남습니다 · 기기를 옮겨도 따라옵니다</p>
      </div>
    </WidgetCard>
  );
}

/**
 * 근태 관리 위젯 (#2b) — 출퇴근 + 이번 달 요약 + 월별 사용 추이.
 *
 * 와이어프레임의 '근무일수 · 누적 근무' 는 서버에 대응 데이터가 없어(출퇴근 API 부재)
 * 실제로 조회 가능한 값 — 초과근무 신청 시간 · 연차 사용/잔여 — 로 채웠다.
 * 막대는 `LeaveSummaryDto.monthlyUsage`(월별 연차 사용) 실데이터.
 *
 * 연차 요약은 휴가 위젯과 **같은 캐시 키**를 쓴다 → 두 위젯을 같이 얹어도 조회는 1회.
 */
export function AttendanceSummaryWidget() {
  return DASHBOARD_V2_ENABLED ? <AttendanceSummaryWidgetV2 /> : <AttendanceSummaryWidgetLegacy />;
}

function AttendanceSummaryWidgetLegacy() {
  const year = new Date().getFullYear();
  const month = new Date().getMonth();
  const leaveQ = useLeaveSummary();
  const overtimeQ = useOvertime();
  const loading = leaveQ.loading || overtimeQ.loading;
  const error = leaveQ.error ?? overtimeQ.error;
  const data = leaveQ.data ? { leave: leaveQ.data, overtime: overtimeQ.data ?? [] } : undefined;

  const daily = data?.leave.dailyMinutes;
  const monthly = data?.leave.monthlyUsage ?? [];
  // 이번 달까지 최근 6개월 — 막대 하나가 한 달.
  const recent = monthly.slice(Math.max(0, month - 5), month + 1);
  const peak = Math.max(1, ...recent);
  const overtimeHours = (data?.overtime ?? [])
    .filter((o) => new Date(o.date).getFullYear() === year)
    .reduce((sum, o) => sum + (Number(o.hours) || 0), 0);

  return (
    <WidgetCard title="근태 관리" tail={`${year}.${String(month + 1).padStart(2, '0')}`} loading={loading} error={error}>
      <div className="dw-stack">
        <PunchBlock compact />
        <div className="dw-divider" />
        <div className="dw-kv-list">
          {/* 분이 진실이라 반반차를 쓰면 "14일 6시간" 처럼 남는다 — 옛 서버면 예전처럼 일수. */}
          <div className="dw-kv"><span>잔여 연차</span><b>{leaveTotalText(leaveAvailable(data?.leave.annual) ?? 0, leaveAvailable(data?.leave.annualMinutes), daily)}</b></div>
          <div className="dw-kv"><span>사용 연차</span><b>{leaveTotalText(data?.leave.annual.used ?? 0, data?.leave.annualMinutes?.used, daily)}</b></div>
          <div className="dw-kv"><span>결재 대기</span><b>{leaveTotalText(data?.leave.annual.pending ?? 0, data?.leave.annualMinutes?.pending, daily)}</b></div>
          <div className="dw-kv"><span>초과 근무</span><b className="dw-kv__up">{overtimeHours}시간</b></div>
        </div>
        <div className="dw-spark" aria-hidden>
          {recent.map((v, i) => (
            <i
              key={i}
              className={i === recent.length - 1 ? 'is-current' : undefined}
              style={{ height: `${Math.max(6, (v / peak) * 100)}%` }}
            />
          ))}
        </div>
        <div className="dw-spark__cap">최근 6개월 연차 사용</div>
      </div>
    </WidgetCard>
  );
}

/* ── 대시보드 시안 v2 V3b (2026-09-16) — 근태 관리가 보이는 항목을 읽는 판 ───────────────────────── */

/**
 * 분 → 「7시간」 · 「7시간 30분」 · **0 이면 「0시간」**.
 *
 * ⛔ **이 자리는 «일부러» 공용 `formatWorkMinutes` 를 안 쓴다** (2026-09-26 판정).
 *   공용 함수는 0 을 「0분」, 30분을 「30분」으로 적어 **단위가 칸마다 갈린다.**
 *   여기는 **단위를 「시간」으로 고정**한다 — 까닭이 아래 `:363` 의 «구조»에 이미 있다:
 *
 *     kv(key, '누적 근무', dailyQ.data ? minutesText(…) : '—')
 *                                        └ **「모른다」는 `'—'` 로 «따로» 낸다**
 *
 *   ⇒ 그러니 「0시간」은 **«값이 0 이다»** 라는 뜻이다. 브리핑 KPI(`BriefingPage.tsx:37`)의
 *     「0 은 «모른다»가 아니라 값이다」와 **같은 갈래**다.
 * ⛔ **언제 푸나:** 이 화면이 `'—'` 와 「0시간」을 **구별하지 않게 되는 날.**
 *   (정본 = `lib/daily-work.ts` 의 `formatWorkMinutes` · 면제 목록은 `lib/duration-format.test.ts`)
 * ⚠ 바로 위 `:156` 의 `hm`(「7h 30m」)은 **어휘가 다른 계통**이라 이 이야기의 대상이 아니다.
 */
const minutesText = (min: number) => `${Math.floor(min / 60)}시간${min % 60 ? ` ${min % 60}분` : ''}`;

/**
 * 근태 관리 v2 — 보이는 항목 8종 · 순서 (깃발 켤 때만).
 *
 * ★★★ **설정이 없으면 옛 판과 같은 모양** — 출퇴근 · 잔여 · 사용 · 결재 대기 · 초과 근무 · 6개월 막대(V1 규칙 D1).
 * ★★ 새 항목의 값은 이미 있는 서버 칸에서 온다 — 근무일수 · 지각 = 월 요약 · 누적 근무 = 날별 집계. 모르면 「—」.
 *    회사가 지각 판정을 안 쓰면(`lateJudged === false`) 「쓰지 않음」 — 0회로 적지 않는다. 조퇴 수는 서버 칸이 없다(DR8).
 * ★ 켜지 않은 항목의 데이터는 부르지 않는다.
 * ★ 초과 근무는 옛 판처럼 **올해 합**이다 — 기간 「올해」는 서버 전이라 설정에서 막혀 있다.
 */
export function AttendanceSummaryWidgetV2() {
  const items = useWidgetItems('attendance-summary');
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const order = items.visible('items');

  const leaveQ = useLeaveSummary();
  const overtimeQ = useOvertime();
  const summaryQ = useAttendanceSummary(year, month + 1, order.includes('workDays') || order.includes('late'));
  const dailyQ = useDailyWorkMonth(year, month + 1, order.includes('workedTotal'));
  const loading = leaveQ.loading || overtimeQ.loading;
  const error = leaveQ.error ?? overtimeQ.error;

  const leave = leaveQ.data;
  const daily = leave?.dailyMinutes;
  const monthly = leave?.monthlyUsage ?? [];
  const recent = monthly.slice(Math.max(0, month - 5), month + 1);
  const peak = Math.max(1, ...recent);
  const overtimeHours = (overtimeQ.data ?? [])
    .filter((o) => new Date(o.date).getFullYear() === year)
    .reduce((sum, o) => sum + (Number(o.hours) || 0), 0);

  const blocks: JSX.Element[] = [];
  let kvs: JSX.Element[] = [];
  const flush = () => {
    if (kvs.length === 0) return;
    blocks.push(<div key={`kv-${blocks.length}`} className="dw-kv-list">{kvs}</div>);
    kvs = [];
  };
  const kv = (key: string, label: string, value: string, up = false) => {
    kvs.push(<div key={key} className="dw-kv"><span>{label}</span><b className={up ? 'dw-kv__up' : undefined}>{value}</b></div>);
  };

  order.forEach((key, i) => {
    switch (key) {
      case 'punch':
        flush();
        blocks.push(<PunchBlock key="punch" compact />);
        if (i < order.length - 1) blocks.push(<div key="punch-divider" className="dw-divider" />);
        break;
      case 'leaveRemaining':
        kv(key, '잔여 연차', leaveTotalText(leaveAvailable(leave?.annual) ?? 0, leaveAvailable(leave?.annualMinutes), daily));
        break;
      case 'leaveUsed':
        kv(key, '사용 연차', leaveTotalText(leave?.annual.used ?? 0, leave?.annualMinutes?.used, daily));
        break;
      case 'leavePending':
        kv(key, '결재 대기', leaveTotalText(leave?.annual.pending ?? 0, leave?.annualMinutes?.pending, daily));
        break;
      case 'overtime':
        kv(key, '초과 근무', `${overtimeHours}시간`, true);
        break;
      case 'workDays':
        kv(key, '근무일수', summaryQ.data ? `${summaryQ.data.workedDays}일` : '—');
        break;
      case 'workedTotal':
        kv(key, '누적 근무', dailyQ.data ? minutesText(dailyQ.data.total.creditedMinutes) : '—');
        break;
      case 'late':
        kv(key, '지각·조퇴', !summaryQ.data ? '—' : summaryQ.data.lateJudged === false ? '쓰지 않음' : `지각 ${summaryQ.data.lateDays}회`);
        break;
      case 'sixMonths':
        flush();
        blocks.push(
          <div key="spark" className="dw-spark" aria-hidden>
            {recent.map((v, j) => (
              <i
                key={j}
                className={j === recent.length - 1 ? 'is-current' : undefined}
                style={{ height: `${Math.max(6, (v / peak) * 100)}%` }}
              />
            ))}
          </div>,
          <div key="spark-cap" className="dw-spark__cap">최근 6개월 연차 사용</div>,
        );
        break;
      default:
        break;
    }
  });
  flush();

  return (
    <WidgetCard
      title="근태 관리"
      tail={`${year}.${String(month + 1).padStart(2, '0')}`}
      loading={loading}
      error={error}
      empty={blocks.length === 0 ? '보여 줄 항목이 없습니다 — 위젯 설정에서 켜세요' : null}
    >
      <div className="dw-stack">{blocks}</div>
    </WidgetCard>
  );
}
